import { useEffect, useState } from 'react';
import { X, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { withDemoFlag } from '../demoMode';
import { crmStore } from '../store';
import { formatEUR, formatDateFR } from '../utils';
import { round2, statutPaiement } from '../utils/finance';
import type { Facture } from '../types';

type Row = { id: string; date_paiement: string; montant: number; mode: string | null; reference: string | null; commentaire: string | null };

interface Props {
  facture: Facture;
  /** Montant TTC dû après acomptes et avoirs. */
  totalDu: number;
  modes: string[];
  onClose: () => void;
}

export function PaiementModal({ facture, totalDu, modes, onClose }: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [montant, setMontant] = useState('');
  const [mode, setMode] = useState(facture.modePaiement || modes[0] || 'Virement bancaire');
  const [reference, setReference] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await supabase.from('paiements').select('*').eq('facture_id', facture.id).order('date_paiement');
    setRows((data as any) || []);
  };
  useEffect(() => { load(); }, [facture.id]);

  const encaisse = round2(rows.reduce((s, r) => s + Number(r.montant || 0), 0));
  const reste = round2(totalDu - encaisse);
  const prochaine = (facture.echeancier || []).find((e) => !e.paid && e.date);

  const sync = async (list: Row[]) => {
    const total = round2(list.reduce((s, r) => s + Number(r.montant || 0), 0));
    const st = statutPaiement(totalDu, total, facture.dateEcheance) || (facture.statut === 'Payée' || facture.statut === 'Partiellement payée' ? 'Émise' : facture.statut);
    const last = list.length ? list[list.length - 1].date_paiement : null;
    await supabase.from('factures').update({ montant_paye: total, statut: st as any, date_paiement: st === 'Payée' ? last : null }).eq('id', facture.id);
    await crmStore.reload();
  };

  const add = async () => {
    const m = Number(String(montant).replace(',', '.'));
    if (!m || m <= 0) { toast.error('Montant invalide'); return; }
    if (!date) { toast.error('Date obligatoire'); return; }
    if (m > reste + 0.005) { toast.error(`Montant supérieur au reste à payer (${formatEUR(reste)})`); return; }
    setBusy(true);
    const { error } = await supabase.from('paiements').insert(withDemoFlag({ facture_id: facture.id, date_paiement: date, montant: m, mode, reference: reference || null, commentaire: commentaire || null }));
    if (error) { setBusy(false); toast.error(`Erreur : ${error.message}`); return; }
    const { data } = await supabase.from('paiements').select('*').eq('facture_id', facture.id).order('date_paiement');
    setRows((data as any) || []);
    await sync((data as any) || []);
    setMontant(''); setReference(''); setCommentaire(''); setBusy(false);
    toast.success('Paiement enregistré');
  };

  const remove = async (id: string) => {
    await supabase.from('paiements').delete().eq('id', id);
    const list = rows.filter((r) => r.id !== id);
    setRows(list);
    await sync(list);
  };

  const inputCls = 'w-full px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-md text-sm text-slate-200 focus:outline-none focus:border-amber-500/50';

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg text-slate-100">Paiements — <span className="font-mono text-amber-300">{facture.numero}</span></h2>
          <button onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-slate-200"><X size={18} strokeWidth={1.5} /></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5 text-sm">
          <div className="p-3 rounded-lg bg-slate-950/60"><div className="text-[10px] uppercase tracking-widest text-slate-500">Total dû TTC</div><div className="text-slate-100">{formatEUR(totalDu)}</div></div>
          <div className="p-3 rounded-lg bg-slate-950/60"><div className="text-[10px] uppercase tracking-widest text-slate-500">Déjà encaissé</div><div className="text-emerald-400">{formatEUR(encaisse)}</div></div>
          <div className="p-3 rounded-lg bg-slate-950/60"><div className="text-[10px] uppercase tracking-widest text-slate-500">Reste à payer</div><div className="text-amber-400">{formatEUR(reste)}</div></div>
          <div className="p-3 rounded-lg bg-slate-950/60"><div className="text-[10px] uppercase tracking-widest text-slate-500">Prochaine échéance</div><div className="text-slate-200">{prochaine?.date ? formatDateFR(prochaine.date) : formatDateFR(facture.dateEcheance)}</div></div>
        </div>

        {rows.length > 0 && (
          <table className="w-full text-sm mb-5">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500"><th className="py-1">Date</th><th>Montant</th><th>Mode</th><th>Référence</th><th></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-800 text-slate-300">
                  <td className="py-1.5">{formatDateFR(r.date_paiement)}</td><td>{formatEUR(Number(r.montant))}</td><td>{r.mode}</td>
                  <td title={r.commentaire || ''}>{r.reference}</td>
                  <td className="text-right"><button onClick={() => remove(r.id)} aria-label="Supprimer le paiement" className="p-1 text-slate-500 hover:text-red-400"><Trash2 size={14} strokeWidth={1.5} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {reste > 0.005 ? (
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-400">Date<input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></label>
            <label className="text-xs text-slate-400">Montant (€)
              <div className="flex gap-2"><input value={montant} onChange={(e) => setMontant(e.target.value)} inputMode="decimal" className={inputCls} placeholder="0,00" />
                <button type="button" onClick={() => setMontant(String(reste))} className="px-2 text-xs rounded-md bg-slate-800 text-slate-300 whitespace-nowrap">Solde</button></div>
            </label>
            <label className="text-xs text-slate-400">Mode<select value={mode} onChange={(e) => setMode(e.target.value)} className={inputCls}>{(modes.length ? modes : ['Virement bancaire', 'Chèque', 'Espèces', 'Carte bancaire']).map((m) => <option key={m}>{m}</option>)}</select></label>
            <label className="text-xs text-slate-400">Référence<input value={reference} onChange={(e) => setReference(e.target.value)} className={inputCls} placeholder="N° chèque, virement…" /></label>
            <label className="text-xs text-slate-400 col-span-2">Commentaire<input value={commentaire} onChange={(e) => setCommentaire(e.target.value)} className={inputCls} /></label>
            <button disabled={busy} onClick={add} className="col-span-2 flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg text-sm font-medium disabled:opacity-50">
              <Check size={16} strokeWidth={1.5} /> Valider le paiement
            </button>
          </div>
        ) : <p className="text-sm text-emerald-400">Facture entièrement réglée.</p>}
      </div>
    </div>
  );
}
